import React, { useEffect, useMemo, useRef, useState } from 'react';

type TimeRange = 'none' | '24h' | '7d' | 'custom';

export interface FiltersModalValues {
  severityMin: number;
  timeRange: TimeRange;
  from?: string; // yyyy-mm-dd (local date input value)
  to?: string;   // yyyy-mm-dd (local date input value)
}

interface FiltersModalProps {
  open: boolean;
  initial: FiltersModalValues;
  onApply: (values: FiltersModalValues) => void;
  onClose: () => void;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const FiltersModal: React.FC<FiltersModalProps> = ({ open, initial, onApply, onClose }) => {
  const [severityMin, setSeverityMin] = useState<number>(initial.severityMin ?? 50);
  const [timeRange, setTimeRange] = useState<TimeRange>(initial.timeRange ?? 'none');
  const [from, setFrom] = useState<string | undefined>(initial.from);
  const [to, setTo] = useState<string | undefined>(initial.to);

  const firstFocusableRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setSeverityMin(initial.severityMin ?? 50);
      setTimeRange(initial.timeRange ?? 'none');
      setFrom(initial.from);
      setTo(initial.to);
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    // focus first control
    setTimeout(() => {
      firstFocusableRef.current?.focus();
    }, 0);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const validCustomRange = useMemo(() => {
    if (timeRange !== 'custom') return true;
    if (!from || !to) return false;
    const f = new Date(from);
    const t = new Date(to);
    return !isNaN(f.getTime()) && !isNaN(t.getTime()) && f.getTime() <= t.getTime();
  }, [timeRange, from, to]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="filters-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
      onMouseDown={(e) => {
        // close when clicking backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/30" />

      <div
        className="relative bg-white w-[420px] max-w-[90vw] rounded-2xl shadow-xl border border-gray-200 p-5"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 id="filters-title" className="text-[16px] font-semibold text-gray-900">Filters</h2>
        </div>

        {/* Severity */}
        <div className="mb-6">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-[14px] font-semibold text-gray-900">Severity</div>
              <div className="text-[12px] text-gray-500">Filter based on the severity score of each signal</div>
            </div>
            <div className="text-[12px] font-semibold text-gray-800">{clamp(severityMin, 0, 100)} - 100</div>
          </div>

          {/* Custom slider styled to match design, with accessible range input overlay */}
          <div className="relative h-8 select-none">
            {/* Track */}
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[10px] rounded-full bg-[#ECECEC] shadow-inner" />
            {/* Filled portion */}
            <div
              className="absolute top-1/2 -translate-y-1/2 h-[10px] rounded-full bg-[#FFD400]"
              style={{ width: `${clamp(severityMin, 0, 100)}%` }}
            />
            {/* Thumb */}
            <div
              className="absolute top-1/2 -translate-y-1/2 -ml-[10px] w-5 h-5 rounded-full bg-[#FFD400] shadow"
              style={{ left: `${clamp(severityMin, 0, 100)}%` }}
            />
            {/* Hidden but interactive range input for accessibility and keyboard control */}
            <input
              ref={firstFocusableRef}
              type="range"
              min={0}
              max={100}
              step={1}
              value={clamp(severityMin, 0, 100)}
              onChange={(e) => setSeverityMin(Number(e.target.value))}
              aria-label="Minimum severity"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
        </div>

        {/* Time Received */}
        {/* <div className="mb-6">
          <div className="text-[13px] font-semibold text-gray-900 mb-2">Time Received</div>
          <div className="space-y-2 text-[13px]">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="timeRange"
                value="none"
                checked={timeRange === 'none'}
                onChange={() => setTimeRange('none')}
              />
              <span>No time filter</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="timeRange"
                value="24h"
                checked={timeRange === '24h'}
                onChange={() => setTimeRange('24h')}
              />
              <span>Last 24 Hours</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="timeRange"
                value="7d"
                checked={timeRange === '7d'}
                onChange={() => setTimeRange('7d')}
              />
              <span>Last 7 Days</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="timeRange"
                value="custom"
                checked={timeRange === 'custom'}
                onChange={() => setTimeRange('custom')}
              />
              <span>Custom Date Range</span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Start</label>
              <input
                type="date"
                className="w-full border rounded-md px-2 py-1 text-[13px] disabled:bg-gray-100"
                value={from || ''}
                onChange={(e) => setFrom(e.target.value || undefined)}
                disabled={timeRange !== 'custom'}
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">End</label>
              <input
                type="date"
                className="w-full border rounded-md px-2 py-1 text-[13px] disabled:bg-gray-100"
                value={to || ''}
                onChange={(e) => setTo(e.target.value || undefined)}
                disabled={timeRange !== 'custom'}
              />
            </div>
          </div>

          {timeRange === 'custom' && !validCustomRange && (
            <div className="text-[12px] text-red-600 mt-2">Start date must be before or equal to end date.</div>
          )}
        </div> */}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-[13px] border rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-[13px] rounded-md bg-[#4342FF] text-white disabled:opacity-50"
            disabled={timeRange === 'custom' && !validCustomRange}
            onClick={() => onApply({ severityMin: clamp(severityMin, 0, 100), timeRange, from, to })}
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiltersModal;
